/**
 * StockLevelsPage — the degraded bulk on-hand read surface (#1151), migrated
 * onto the config-driven `ResourcePage` framework (#1392 P4).
 *
 * The list itself is fully described by `stockLevelResource`; this component is
 * the thin wrapper that supplies the one thing the framework has no slot for:
 * the persistent "on-hand unavailable" notice (no batch on-hand endpoint yet —
 * owner decision #935/#936). It composes its own `PageContainer` and renders
 * `ResourcePage` with `container={false}`, so the two share a single app-wide
 * frame — the same wrapper pattern `WebhookEventsPage` uses for its tab strip.
 * The notice sits above the list header (banners belong at the top of the
 * frame); everything else — pagination, sort, search, empty/error states, CSV
 * toolbar, the em-dash on-hand cell — comes from the framework.
 *
 * Permission gate (#465 AND-gate): route-level `<PermissionGuard>` in
 * `App.tsx`.
 */
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Card, CardContent, PageContainer, ResourcePage } from '@resource-kit/ui/react';
import { NavGroupTabs } from '@components/layout/NavGroupTabs';
import { stockLevelResource } from '../stockLevelResource';

export function StockLevelsPage() {
  const { t } = useTranslation();

  return (
    <PageContainer>
      {/* Degraded on-hand notice: the batch on-hand snapshot endpoint has no
          `/api` rebuild yet (owner blocker #935/#936). The framework list has
          no page-body notice slot, so it lives here in the wrapper. */}
      <Card className="border-warn">
        <CardContent className="p-4 flex items-start gap-3" data-testid="stock-onhand-unavailable">
          <Info className="w-5 h-5 text-warn shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">{t('inventory.stock.onHandUnavailableTitle')}</p>
            <p className="text-muted-foreground">{t('inventory.stock.onHandUnavailableDetail')}</p>
          </div>
        </CardContent>
      </Card>

      <ResourcePage
        bundle={{
          ...stockLevelResource,
          host: {
            ...stockLevelResource.host,
            listTabs: () => <NavGroupTabs group="ingredientsStock" />,
          },
        }}
        container={false}
      />
    </PageContainer>
  );
}
